import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { Layout } from './components/Layout'
import { ProtectedRoute } from './components/ProtectedRoute'
import { RouteErrorBoundary } from './components/RouteErrorBoundary'
import { ComponentsExamples } from './modules/componentsExamples'
import { CyclesPage, ManageCyclePage } from './modules/cycles'
import {
	CycleAdminsTab,
	CycleAiSummariesTab,
	CycleBuildCycleTab,
	CycleEvaluationsTab,
	CycleGeneralTab,
	CycleHierarchyTab,
	CycleHoldManagementTab,
	CycleLaunchCycleTab,
	CycleParticipantsTab,
	CycleReportFiltersTab,
	CycleReviewersConfigurationTab,
	CycleReviewersInstructionsTab,
	CycleReviewersListEditReviewers,
	CycleReviewersListTab,
	CycleReviewersTab,
	CycleReviewersUnsolicitedTab,
	CycleRolesPermissionsTab,
	CycleRolesTab,
	CycleStepsTab,
	CycleViewersTab,
	HrReviewFlagsTab,
} from './modules/cycles/tabs'
import { CycleEmailsTab } from './modules/cycles/tabs/CycleEmailsTab'
import { CycleEmailGlobalExclusionsTab } from './modules/cycles/tabs/cycle-emails/CycleEmailGlobalExclusionsTab'
import { CycleEmailHistoryLogTab } from './modules/cycles/tabs/cycle-emails/CycleEmailHistoryLogTab'
import { CycleEmails } from './modules/cycles/tabs/cycle-emails/CycleEmails'
import { CycleEmailsFooterTab } from './modules/cycles/tabs/cycle-emails/CycleEmailsFooterTab'
import { CycleEmailsHistoryTab } from './modules/cycles/tabs/cycle-emails/CycleEmailsHistoryTab'
import { CycleSendManualEmailModal } from './modules/cycles/tabs/cycle-emails/CycleSendManualEmailModal'
import {
	CycleReviewersAddModal,
	CycleReviewersFileUploadModal,
} from './modules/cycles/tabs/cycle-reviewers/CycleReviewersListTab'
import { ManageUnsolicitedReviewers } from './modules/cycles/tabs/cycle-reviewers/unsolicited/ManageUnsolicitedReviewers'
import { CycleAddRoles, CycleMapRoles, CycleUnmappedRoles } from './modules/cycles/tabs/cycle-roles'
import { AggregationPortalPage } from './modules/dealteam/aggregation/AggregationPortalPage'
import { DealDetailsPage } from './modules/dealteam/aggregation/DealDetailsPage'
import { ParticipantDealReportPage } from './modules/dealteam/aggregation/ParticipantDealReportPage'
import { EmailTemplatesPage, ManageEmailTemplate } from './modules/emailTemplates'
import {
	EnpsConfigurationTab,
	EnpsEmailTemplateTab,
	EnpsGeneralTab,
	EnpsLaunchTab,
	EnpsPage,
	EnpsPopulationTab,
	EnpsResponsePage,
	EnpsResultsTab,
	EnpsScheduleTab,
	EnpsViewersTab,
	ManageEnpsPage,
} from './modules/enps'
import { FeatureFlagsPage } from './modules/featureFlags/FeatureFlagsPage'
import { FormBuilder, FormsPage } from './modules/forms'
import { HierarchiesPage } from './modules/hierarchies'
import { IntegrationsPage } from './modules/integrations'
import { CallbackPage, LoginPage, LogoutPage } from './modules/login'
import { LoopResponsePage } from './modules/loops'
import { CreateOrganizationPage, OrganizationDetailsPage, OrganizationsPage } from './modules/organization'
import { OrganizationOverviewTab, OrganizationProfileTab } from './modules/organization/tabs'
import { OktaTab } from './modules/organization/tabs/OktaTab'
import { OrganizationFeatureFlagsTab } from './modules/organization/tabs/OrganizationFeatureFlagsTab'
import { PermissionTemplatesPage, TemplateFormPage } from './modules/permission-templates'
import { RolesPage } from './modules/roles'
import { GlobalSettingsPage, MaintenanceModePage, ModuleSettingsPage } from './modules/settings'
import { SftpPage } from './modules/sftp/SftpPage'
import { UserFormPage, UsersPageContainer } from './modules/users'
import { TestSentryPage } from './pages/TestSentryPage'

const cycleChildRoutes = [
	{
		index: true,
		element: <Navigate to='general' replace />,
	},
	{
		path: 'general',
		element: <CycleGeneralTab />,
	},
	{
		path: 'admins',
		element: <CycleAdminsTab />,
	},
	{
		path: 'viewers',
		element: <CycleViewersTab />,
	},
	{
		path: 'participants',
		element: <CycleParticipantsTab />,
	},
	{
		path: 'roles',
		element: <CycleRolesTab />,
		children: [
			{
				index: true,
				element: <Navigate to='cycleRoles' replace />,
			},
			{
				path: 'cycleRoles',
				element: <CycleAddRoles />,
			},
			{
				path: 'mapRoles',
				element: <CycleMapRoles />,
			},
			{
				path: 'unmappedRoles',
				element: <CycleUnmappedRoles />,
			},
		],
	},
	{
		path: 'rolesPermissions',
		element: <CycleRolesPermissionsTab />,
	},
	{
		path: 'hierarchies',
		element: <CycleHierarchyTab />,
	},
	{
		path: 'reviewers',
		element: <CycleReviewersTab />,
		children: [
			{
				index: true,
				element: <Navigate to='configuration' replace />,
			},
			{
				path: 'configuration',
				element: <CycleReviewersConfigurationTab />,
			},
			{
				path: 'list',
				element: <CycleReviewersListTab />,
				children: [
					{
						path: 'upload',
						element: <CycleReviewersFileUploadModal />,
					},
					{
						path: 'add',
						element: <CycleReviewersAddModal />,
					},
					{
						path: ':participantExternalId',
						element: <CycleReviewersListEditReviewers />,
					},
				],
			},
			{
				path: 'unsolicited',
				element: <CycleReviewersUnsolicitedTab />,
				children: [
					{
						path: 'create',
						element: <ManageUnsolicitedReviewers />,
					},
					{
						path: ':id',
						element: <ManageUnsolicitedReviewers />,
					},
				],
			},
			{
				path: 'instructions',
				element: <CycleReviewersInstructionsTab />,
			},
		],
	},
	{
		path: 'emails',
		element: <CycleEmailsTab />,
		children: [
			{
				index: true,
				element: <Navigate to='list' replace />,
			},
			{
				path: 'list',
				element: <CycleEmails />,
			},
			{
				path: 'email-history',
				element: <CycleEmailsHistoryTab />,
				children: [
					{
						path: ':historyId',
						element: <CycleEmailHistoryLogTab />,
					},
				],
			},
			{
				path: 'email-footer',
				element: <CycleEmailsFooterTab />,
			},
			{
				path: 'global-exclusions',
				element: <CycleEmailGlobalExclusionsTab />,
			},
			{
				path: 'send-manual-email',
				element: <CycleSendManualEmailModal />,
			},
		],
	},
	{
		path: 'aiSummaries',
		element: <CycleAiSummariesTab />,
	},
	{
		path: 'cycleSteps',
		element: <CycleStepsTab />,
	},
	{
		path: 'manageHolds',
		element: <CycleHoldManagementTab />,
	},
	{
		path: 'buildCycle',
		element: <CycleBuildCycleTab />,
	},
	{
		path: 'reportFilters',
		element: <CycleReportFiltersTab />,
	},
	{
		path: 'launchCycle',
		element: <CycleLaunchCycleTab />,
	},
	{
		path: 'evaluations',
		element: <CycleEvaluationsTab />,
	},
]

const enpsChildRoutes = [
	{ index: true, element: <Navigate to='general' replace /> },
	{ path: 'general', element: <EnpsGeneralTab /> },
	{ path: 'configuration', element: <EnpsConfigurationTab /> },
	{ path: 'schedule', element: <EnpsScheduleTab /> },
	{ path: 'population', element: <EnpsPopulationTab /> },
	{ path: 'email-template', element: <EnpsEmailTemplateTab /> },
	{ path: 'viewers', element: <EnpsViewersTab /> },
	{ path: 'launch', element: <EnpsLaunchTab /> },
	{ path: 'results', element: <EnpsResultsTab /> },
]

export const router = createBrowserRouter([
	{
		path: '/enps/response/:executionId',
		element: <EnpsResponsePage />,
		errorElement: <RouteErrorBoundary />,
	},
	{
		path: '/loops/response/:executionId',
		element: <LoopResponsePage />,
		errorElement: <RouteErrorBoundary />,
	},
	{
		path: '/auth/callback',
		element: <CallbackPage />,
		errorElement: <RouteErrorBoundary />,
	},
	{
		path: '/auth/login',
		element: <LoginPage />,
		errorElement: <RouteErrorBoundary />,
	},
	{
		path: '/auth/logout',
		element: <LogoutPage />,
		errorElement: <RouteErrorBoundary />,
	},
	{
		path: '/',
		element: <Layout />,
		errorElement: <RouteErrorBoundary />,
		children: [
			{
				index: true,
				element: <Navigate to='/organizations' replace />,
			},
			{
				path: 'components',
				element: <ComponentsExamples />,
			},
			{
				element: <ProtectedRoute />,
				children: [
					{
						path: 'feature-flags',
						element: <FeatureFlagsPage />,
					},
					{
						path: 'test-sentry',
						element: <TestSentryPage />,
					},
					{
						path: 'settings',
						children: [
							{
								index: true,
								element: <GlobalSettingsPage />,
							},
							{
								path: 'maintenance-mode',
								element: <MaintenanceModePage />,
							},
						],
					},
					{
						path: 'organizations',
						children: [
							{
								index: true,
								element: <OrganizationsPage />,
							},
							{
								path: 'create',
								element: <CreateOrganizationPage />,
							},
							{
								path: ':organizationId',
								children: [
									{
										element: <OrganizationDetailsPage />,
										children: [
											{
												index: true,
												element: <OrganizationOverviewTab />,
											},
											{
												path: 'overview',
												element: <OrganizationOverviewTab />,
											},
											{
												path: 'profile',
												element: <OrganizationProfileTab />,
											},
											{
												path: 'feature-flags',
												element: <OrganizationFeatureFlagsTab />,
											},
											{
												path: 'okta',
												element: <OktaTab />,
											},
										],
									},
									{
										path: 'module-settings',
										element: <ModuleSettingsPage />,
									},
									{
										path: 'cycles',
										element: <Outlet />,
										children: [
											{
												index: true,
												element: <CyclesPage />,
											},
											{
												path: 'create',
												element: <ManageCyclePage />,
												children: cycleChildRoutes,
											},
											{
												path: ':cycleId',
												element: <ManageCyclePage />,
												children: cycleChildRoutes,
											},
										],
									},
									{
										path: 'enps',
										element: <Outlet />,
										children: [
											{
												index: true,
												element: <EnpsPage />,
											},
											{
												path: 'create',
												element: <ManageEnpsPage />,
												children: enpsChildRoutes,
											},
											{
												path: ':enpsId',
												element: <ManageEnpsPage />,
												children: enpsChildRoutes,
											},
										],
									},
									{
										path: 'hr-review-flags',
										element: <HrReviewFlagsTab />,
									},
									{
										path: 'email-templates',
										element: <Outlet />,
										children: [
											{
												index: true,
												element: <EmailTemplatesPage />,
											},
											{
												path: 'create',
												element: <ManageEmailTemplate />,
											},
											{
												path: ':emailTemplateId',
												element: <ManageEmailTemplate />,
											},
										],
									},
									{
										path: 'users',
										element: <Outlet />,
										children: [
											{
												index: true,
												element: <UsersPageContainer />,
											},
											{
												path: 'create',
												element: <UserFormPage />,
											},
											{
												path: ':userId',
												element: <UserFormPage />,
											},
										],
									},
									{
										path: 'roles',
										element: <Outlet />,
										children: [
											{
												index: true,
												element: <RolesPage />,
											},
											{
												path: ':roleId',
												element: <RolesPage />,
											},
										],
									},
									{
										path: 'hierarchies',
										element: <Outlet />,
										children: [
											{
												index: true,
												element: <HierarchiesPage />,
											},
											{
												path: ':hierarchiesId',
												element: <HierarchiesPage />,
											},
										],
									},
									{
										path: 'forms',
										element: <Outlet />,
										children: [
											{
												index: true,
												element: <FormsPage />,
											},
											{
												path: 'builder',
												element: <FormBuilder />,
											},
											{
												path: ':formsId',
												element: <FormBuilder />,
											},
										],
									},
									{
										path: 'permission-templates',
										element: <Outlet />,
										children: [
											{
												index: true,
												element: <PermissionTemplatesPage />,
											},
											{
												path: 'create',
												element: <TemplateFormPage />,
											},
											{
												path: ':templateId',
												element: <TemplateFormPage />,
											},
										],
									},
									{
										path: 'deal-team-aggregation',
										element: <AggregationPortalPage />,
									},
									{
										path: 'deal-team-aggregation/deals/:cycleId',
										element: <DealDetailsPage />,
									},
									{
										path: 'deal-team-aggregation/deals/:cycleId/participants/:userId/report',
										element: <ParticipantDealReportPage />,
									},
									{
										path: 'integrations',
										element: <IntegrationsPage />,
									},
									{
										path: 'sftp',
										element: <SftpPage />,
									},
								],
							},
						],
					},
				],
			},
		],
	},
	{
		path: '*',
		element: <Navigate to='/' replace />,
	},
])
